import {
  BuildTickValuesOptions,
  CategoryDef,
  GroupMode,
  RowsData,
} from "api/interfaces/chartInterface.interface";
import { WidgetStats } from "api/interfaces/widgetInterface.interface";
import { formatNumber } from "utils/functions";

/** BarGraph와 동일한 margin (구독 그래프 플롯 영역 계산용) */
export const CHART_MARGIN_TOP = 10;
export const CHART_MARGIN_BOTTOM = 30;

/** 다중 토글 선택: activeOrgs에 "all"이 있거나 비어 있으면 전부, 아니면 선택된 key만 */
export function buildKeysFromSelection(
  activeOrgs: string[],
  items: CategoryDef[]
): string[] {
  const keySet = new Set(items.map((c) => c.key));
  return activeOrgs.filter((k) => keySet.has(k));
}

/** width에 따라 몇 개 tick만 보여줄지 결정 */
export function buildTickValues(
  dates: string[],
  width: number,
  options?: BuildTickValuesOptions
) {
  const labelMinWidthPx = options?.pixelsPerTick ?? 80;
  const includeEdgeDates = options?.includeEdgeDates ?? true;

  const n = dates.length;
  if (n <= 1) return dates;

  const safeW = Math.max(0, width);
  const maxFit = Math.max(1, Math.floor(safeW / labelMinWidthPx));

  let indices: number[];

  if (n <= maxFit) {
    indices = Array.from({ length: n }, (_, i) => i);
  } else {
    const step = Math.ceil(n / maxFit);
    const wantIdx = new Set<number>();
    for (let idx = 0; idx < n; idx += step) {
      wantIdx.add(idx);
    }

    indices = [...wantIdx].sort((a, b) => a - b);
    const idxSet = new Set(indices);
    if (idxSet.has(0) && idxSet.has(1)) {
      idxSet.delete(1);
    }
    if (n >= 2 && idxSet.has(n - 2) && idxSet.has(n - 1)) {
      idxSet.delete(n - 2);
    }
    indices = [...idxSet].sort((a, b) => a - b);
  }

  if (!includeEdgeDates && n >= 2) {
    indices = indices.filter((i) => i !== 0 && i !== n - 1);
  }

  return indices.map((i) => dates[i]).filter(Boolean);
}

/** x축 라벨 포맷 (이전에는 끝 tick을 비워 겹침을 피했으나, 양 끝 날짜도 보이도록 그대로 표시) */
export const getTickValuesFormat = (_tickValues: string[], v: string) => {
  return String(v);
};

// y축 tick을 "최대 5단계"로 보기 좋게 만들기 (0 포함, 중복 제거)
export function buildNiceTicksMax5(maxY: number) {
  const safeMax = Number.isFinite(maxY) ? maxY : 0;
  if (safeMax <= 0) return [0];

  const rawStep = safeMax / 5;
  const pow = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const candidates = [1, 2, 5, 10].map((m) => m * pow);
  const step =
    Math.trunc(
      candidates.find((c) => c >= rawStep) ?? candidates[candidates.length - 1]
    ) || 1;

  const top = Math.ceil(safeMax / step) * step;

  const ticks: number[] = [];
  for (let v = 0; v <= top; v += step) ticks.push(v);
  return ticks;
}

/** 작은 값도 2~5px 4단계로 보이도록 표시값 보정 (툴팁은 실제값). grouped=키별 max 기준, stacked=행 합계 기준 */
const MIN_BAR_RATIO = 5 / 200;
const MAX_PX_STEP = 4;
/** 단계별 비율: 최소 3px(0.6) ~ 5px(1.0) 보이도록 */
const STEP_RATIOS = [0.6, 0.8, 0.9, 1.0];

/** value를 minDisplay(5px 기준) 대비 3~5px 단계로 매핑. 아주 작은 값도 최소 3px는 보이도록 */
function toStepDisplayValue(value: number, minDisplay: number): number {
  if (minDisplay <= 0) return value;
  const stepIndex = Math.max(
    0,
    Math.min(MAX_PX_STEP - 1, Math.floor((value / minDisplay) * MAX_PX_STEP))
  );
  const ratio = STEP_RATIOS[stepIndex];
  return minDisplay * ratio;
}

export const getMinDisplayValue = (
  rows: RowsData[],
  keys: string[],
  groupMode: GroupMode = "stacked"
) => {
  return rows.map((row: Record<string, unknown>) => {
    const out = { ...row };
    const rowTotal = keys.reduce(
      (sum, key) => sum + (Number(row[key]) || 0),
      0
    );
    if (rowTotal <= 0) return out;
    const minDisplay = rowTotal * MIN_BAR_RATIO;
    keys.forEach((key) => {
      const val = Number(row[key]);
      if (val <= 0 || !Number.isFinite(val)) return;
      const displayVal =
        val < minDisplay ? toStepDisplayValue(val, minDisplay) : val;
      (out as Record<string, number>)[key] = displayVal;
    });
    return out;
  });
};

export const getActualValue = (
  id: string,
  indexValue: string,
  rows: WidgetStats[],
  indexBy: string = "date"
): number => {
  const rawRow = rows.find(
    (r) =>
      String((r as unknown as Record<string, unknown>)[indexBy]) === indexValue
  );
  if (!rawRow) return 0;
  const val = (rawRow as unknown as Record<string, unknown>)[id];
  return Number(val);
};

export const findSerieItem = (serieId: string | number, data: CategoryDef[]) => {
  const id = String(serieId);
  return [...data]
    .sort((a, b) => b.key.length - a.key.length)
    .find(
      (item) =>
        id.startsWith(item.key) &&
        /^\d+$/.test(id.slice(item.key.length))
    );
};

export const convertToShortFormat = (v: number) => {
  return v > 999 ? `${formatNumber(v / 1000)}K` : formatNumber(v);
};
